import {afterNextRender, Directive, ElementRef, inject, OnDestroy} from '@angular/core';

/**
 * Fades a section up as it scrolls into view.
 *
 * Deliberately opt-in from the browser side: the element ships **visible** in the prerendered
 * HTML and only gets the animation class once JavaScript runs. Do it the other way round — hide
 * in CSS, reveal with JS — and a crawler or a reader without JS gets a blank page, which on a
 * site whose whole point is being readable by crawlers would be a self-inflicted wound.
 *
 * Honours `prefers-reduced-motion` by simply not animating.
 */
@Directive({
    selector: '[cbReveal]',
})
export class RevealDirective implements OnDestroy {

    private observer?: IntersectionObserver;

    constructor() {
        const host = inject(ElementRef<HTMLElement>).nativeElement as HTMLElement;

        afterNextRender(() => {
            const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
            if (reduced || typeof IntersectionObserver === 'undefined') {
                return;
            }

            // Anything already on screen when JS runs has been painted for a while. Hiding it now
            // just to fade it back in is a flash, not an animation — so it keeps the state it is
            // already in and only the sections still below the fold get the class.
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            if (host.getBoundingClientRect().top < viewportHeight) {
                return;
            }

            host.classList.add('cb-reveal');
            this.observer = new IntersectionObserver(entries => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('cb-revealed');
                        this.observer?.unobserve(entry.target);
                    }
                }
            }, {rootMargin: '0px 0px -12% 0px'});

            this.observer.observe(host);
        });
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();
    }
}
